import React from 'react';
import '../styles/index.scss';

import { Box, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Card, CardHeader, CardContent, Avatar, ForgeIcon, ThumbsDownIcon, ThumbsUpIcon, OpenInNewIcon } from './ui';

import config from '../utils/config.json';
import formatProductName from '../utils/formatProductName';

const MessageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
    [theme.breakpoints.down('lg')]: {
      marginLeft: 0,
      padding: theme.spacing(1),
    },
}));

const getVidyardEmbedUrl = (url) => {
    const match = url.match(/vidyard\.com\/([a-zA-Z0-9]+)/);
    if (match && match[1]) {
        return `https://play.vidyard.com/${match[1]}.html`;
    }
    return null;
};

const ChatResponse = ({
    answerTitle,
    answerIntro,
    answerBodyArray,
    answerConclusion,
    answerSource,
    language,
    product,
    version,
    id,
}) => {

    console.log("answer title:", answerTitle);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const displayProduct = formatProductName(product);

    const firstVideo = answerSource.find((source_document) =>
        source_document["url"].startsWith("https://play.vidyard.com")
    );
    const vidyardEmbedUrl = firstVideo ? getVidyardEmbedUrl(firstVideo["url"]) : null;

    return (
        <MessageContainer aria-live='polite'>
            <Card
                className="mt-6"
                sx={{
                    borderLeft: '4px solid #E20000',
                    boxShadow: '0 2px 16px rgba(51,0,0,0.08)',
                    borderRadius: '0 8px 8px 0',
                }}
            >
                <CardHeader
                    avatar={<Avatar icon={<ForgeIcon />} size="sm" />}
                    subtitle={`HelpBot - ${displayProduct} ${version} • Question ID: ${id}`}
                    title={answerTitle}
                    titleVariant="default"
                    sx={{
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                        '& .MuiCardHeader-title': { fontWeight: 600, color: '#330000' },
                        '& .MuiCardHeader-subheader': { fontSize: '0.75rem' },
                    }}
                />

                <CardContent top="none">
                    <Typography variant="body1" paragraph>
                        {answerIntro}
                    </Typography>
                    {answerBodyArray.map((answer, index) => (
                        <Typography variant="body1" key={index}>
                            {answer}
                            <br />
                        </Typography>
                    ))}
                    <Typography variant="body1" paragraph>
                        <br />
                        {answerConclusion}
                    </Typography>

                    {vidyardEmbedUrl && (
                        <Box sx={{ display: 'grid', placeItems: 'center', mb: 2.5 }}>
                            <iframe
                                title="Vidyard Video"
                                src={vidyardEmbedUrl}
                                width="640"
                                height="360"
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                            />
                        </Box>
                    )}

                    {/* Divider with References label */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mt: 2,
                        mb: 1.5,
                    }}>
                        <Typography variant="overline" sx={{ color: '#330000', fontWeight: 700, letterSpacing: 1 }}>
                            References
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(51,0,0,0.15)' }} />
                    </Box>

                    {/* Reference chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                        {answerSource.map((source_document, index2) => {
                            const pageNumberRange = source_document['pageNumberRange'];
                            const isValidPageNumber = pageNumberRange && /^\d+$/.test(pageNumberRange);
                            const href = isValidPageNumber
                                ? `${source_document['url']}#page=${pageNumberRange}`
                                : source_document['url'];
                            const label = source_document['documentTitle'].replace(/_/g, ' ');
                            const pageLabel = isValidPageNumber
                                ? ` · ${config.languages.find(lang => lang.code === language).texts.page} ${pageNumberRange}`
                                : '';

                            return (
                                <Box
                                    key={index2}
                                    component="a"
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        border: '1px solid #330000',
                                        borderRadius: '20px',
                                        px: 1.5,
                                        py: 0.5,
                                        color: '#330000',
                                        fontSize: '0.75rem',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: '#330000',
                                            color: '#fff',
                                        },
                                    }}
                                >
                                    <OpenInNewIcon style={{ fontSize: 12 }} />
                                    {label}{pageLabel}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Feedback row */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isSmallScreen ? 'flex-start' : 'flex-end',
                        gap: 1,
                        pt: 1,
                        borderTop: '1px solid rgba(0,0,0,0.06)',
                    }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                            How were these results?
                        </Typography>
                        <IconButton
                            aria-label="Like"
                            size="small"
                            sx={{
                                // border: '1.5px solid rgba(51,0,0,0.3)',
                                color: '#330000',
                                transition: 'all 0.15s ease',
                                '&:hover': { backgroundColor: '#330000', color: '#fff', borderColor: '#330000' },
                            }}
                        >
                            <ThumbsUpIcon />
                        </IconButton>
                        <IconButton
                            aria-label="Dislike"
                            size="small"
                            sx={{
                                // border: '1.5px solid rgba(51,0,0,0.3)',
                                color: '#330000',
                                transition: 'all 0.15s ease',
                                '&:hover': { backgroundColor: '#330000', color: '#fff', borderColor: '#330000' },
                            }}
                        >
                            <ThumbsDownIcon />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        </MessageContainer>
    );
};

export default ChatResponse;
